<script>
    import BetaTag from "../components/BetaTag.svelte";
    import {
        Navbar,
        NavBrand,
        NavHamburger,
        NavLi,
        NavUl,
        Dropdown,
        DropdownItem,
    } from "flowbite-svelte";
    import {
        ChevronDownOutline,
        QuestionCircleOutline,
    } from "flowbite-svelte-icons";
    import { page } from "$app/stores";
    import { goto } from "$app/navigation";
    import { authenticated_user, enabled_features, startTour } from "../../service/store";
    import { startIndexForSlug } from "../components/tourSteps";
    import LogoIcon from "./assets/logo.png";
    import DarkModeToggle from "../components/DarkModeToggle.svelte";

    // Reactive to client-side navigation (was read once on mount).
    $: slug = $page.url.pathname.split("/").filter(Boolean).pop() ?? "";
    $: isLandingPage = $page.url.pathname === "/" || $page.url.pathname === "/login";


    const moreSlugs = ['notify', 'research', 'defend', 'chat', 'report_checker', 'draft_sa', 'qualify_sa', 'draft_ireland', 'qualify_ireland', 'draft_au', 'innovate_uk', 'project_sorter'];
    $: moreActive = moreSlugs.includes(slug);
</script>

<Navbar class="relative !bg-transparent !border-0 !shadow-none py-4">
    <NavBrand href="/" class="flex items-center !bg-transparent">
        {#if isLandingPage}
            <div class="dark:hidden mr-2 flex-shrink-0" style="width:32px;height:32px;background-color:#f5f2ed;">
                <img src={LogoIcon} alt="logo" width="32px" style="mix-blend-mode: multiply;" />
            </div>
        {:else}
            <img src={LogoIcon} alt="logo" width="32" height="32" class="mr-2 flex-shrink-0 mix-blend-multiply dark:invert dark:mix-blend-screen" />
        {/if}
        <span class="self-center whitespace-nowrap text-xl font-semibold dark:text-white">
            SmartClaim
        </span>
    </NavBrand>
    {#if $authenticated_user?.id}
        <!-- Desktop: pill nav absolutely centred -->
        <div class="hidden md:flex absolute left-1/2 -translate-x-1/2 z-50 items-center gap-1 font-geist">
            <a href="/realtime" data-tour="nav-interview"
               class="px-4 py-1 rounded-full text-base font-medium transition-colors {slug === 'realtime' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}">Interview</a>
            <a href="/qualify" data-tour="nav-qualify"
               class="px-4 py-1 rounded-full text-base font-medium transition-colors {slug === 'qualify' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}">Qualify</a>
            <a href="/draft" data-tour="nav-draft"
               class="px-4 py-1 rounded-full text-base font-medium transition-colors {slug === 'draft' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}">Draft</a>
            <a href="/review" data-tour="nav-review"
               class="px-4 py-1 rounded-full text-base font-medium transition-colors {slug === 'review' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}">Review</a>
            <div class="relative">
                <button id="more-dropdown-btn" data-tour="nav-more"
                    class="flex items-center gap-1.5 px-4 py-1 rounded-full text-base font-medium transition-colors {moreActive ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}">
                    More <ChevronDownOutline class="w-3.5 h-3.5 opacity-60" />
                </button>
                <Dropdown triggeredBy="#more-dropdown-btn" class="z-50 !w-max">
                    <DropdownItem href="/notify" data-tour="more-notify">Notify</DropdownItem>
                    <DropdownItem href="/research" data-tour="more-research">Research</DropdownItem>
                    <DropdownItem href="/defend" data-tour="more-defend">Defend</DropdownItem>
                    <DropdownItem href="/chat" data-tour="more-chat">Chat</DropdownItem>
                    {#if $enabled_features.has("report_checker")}
                        <DropdownItem href="/report_checker" data-tour="more-report-checker">Report Checker <BetaTag /></DropdownItem>
                    {/if}
                    {#if $enabled_features.has("project_sorter")}
                        <DropdownItem href="/project_sorter">Project Sorter <BetaTag /></DropdownItem>
                    {/if}
                    <!-- Temporarily hidden from the More menu
                    <DropdownItem href="/draft_sa">Draft – South Africa</DropdownItem>
                    <DropdownItem href="/qualify_sa">Qualify – South Africa</DropdownItem>
                    <DropdownItem href="/draft_ireland">Draft – Ireland <span class="text-purple-500">(beta)</span></DropdownItem>
                    <DropdownItem href="/qualify_ireland">Qualify – Ireland <span class="text-purple-500">(beta)</span></DropdownItem>
                    -->
                </Dropdown>
            </div>
        </div>

        <!-- Desktop: right side controls -->
        <div class="hidden md:flex ms-auto items-center gap-3 font-geist">
            {#if slug !== ''}
                <DarkModeToggle />
            {/if}
            <button
                class="inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors flex-shrink-0"
                on:click={() => startTour(startIndexForSlug(slug))}
                aria-label="User guide"
                title="User guide"
            >
                <QuestionCircleOutline class="w-5 h-5" />
            </button>
            <button
                class="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                on:click={() => goto("/profile")}
                aria-label="Profile"
            >
                <svg class="w-5 h-5 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M4 20c0-4 3.58-6 8-6s8 2 8 6"/>
                </svg>
            </button>
        </div>

        <!-- Mobile: hamburger + dropdown -->
        <NavHamburger />
        <NavUl class="font-geist md:!hidden" activeUrl={`/${slug}`}>
            <NavLi href="/realtime">Interview</NavLi>
            <NavLi href="/qualify">Qualify</NavLi>
            <NavLi href="/draft">Draft</NavLi>
            <NavLi href="/review">Review</NavLi>
            <NavLi href="/research">Research</NavLi>
            <NavLi href="/defend">Defend</NavLi>
            <NavLi href="/chat">Chat</NavLi>
            <NavLi href="/notify">Notify</NavLi>
            {#if $enabled_features.has("report_checker")}
                <NavLi href="/report_checker">Report Checker <BetaTag /></NavLi>
            {/if}
            {#if $enabled_features.has("project_sorter")}
                <NavLi href="/project_sorter">Project Sorter <BetaTag /></NavLi>
            {/if}
            <li>
                <button
                    class="w-full text-left py-2 px-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    on:click={() => startTour(startIndexForSlug(slug))}
                >
                    User guide
                </button>
            </li>
            <li class="px-3 py-2">
                <button class="w-full flex justify-center" on:click={() => goto("/profile")} aria-label="Profile">
                    <span class="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                        <svg class="w-5 h-5 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                            <circle cx="12" cy="8" r="4"/>
                            <path d="M4 20c0-4 3.58-6 8-6s8 2 8 6"/>
                        </svg>
                    </span>
                </button>
            </li>
        </NavUl>
    {:else}
        <!-- Desktop: links absolutely centered on page, login pushed to right -->
        <nav class="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-8 font-geist">
            <a href="/#features" class="text-sm font-medium text-navy hover:opacity-70 transition">Features</a>
            <a href="/#pricing" class="text-sm font-medium text-navy hover:opacity-70 transition">Pricing</a>
            <a href="/#contact" class="text-sm font-medium text-navy hover:opacity-70 transition">Contact</a>
        </nav>
        <div class="hidden md:flex ms-auto font-geist">
            <button
                class="bg-navy text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition"
                on:click={() => goto("/login")}
            >
                Log in
            </button>
        </div>
        <!-- Mobile: hamburger + dropdown -->
        <NavHamburger />
        <NavUl class="font-geist md:!hidden">
            <NavLi href="/#features" class="!text-navy">Features</NavLi>
            <NavLi href="/#pricing" class="!text-navy">Pricing</NavLi>
            <NavLi href="/#contact" class="!text-navy">Contact</NavLi>
            <li class="px-3 py-2">
                <button
                    class="bg-navy text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition w-full"
                    on:click={() => goto("/login")}
                >
                    Log in
                </button>
            </li>
        </NavUl>
    {/if}
</Navbar>

